import type { JSX } from 'react'
/**
 * Sign-in screen.
 *
 * One button. There is no registration and no password for ordinary users — the
 * identity provider owns all of that.
 */

import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'

const ERRORS: Record<string, string> = {
  guest_account:
    'Guest accounts cannot access the ERP. Sign in with your company account.',
  domain_not_allowed:
    'That account is outside the company domain and cannot access the ERP.',
  wrong_tenant: 'That account belongs to a different organization.',
  inactive: 'Your account has been deactivated. Contact an administrator.',
  invalid_state: 'The sign-in attempt expired. Please try again.',
  exchange_failed: 'The identity provider rejected the sign-in. Please try again.',
  invalid_token: 'The sign-in could not be verified. Please try again.',
  no_object_id: 'The identity provider did not return a usable account id.',
  missing_code: 'The sign-in was cancelled.',
}

export function LoginPage(): JSX.Element {
  const [params] = useSearchParams()
  const [showBreakGlass, setShowBreakGlass] = useState(false)

  const error = params.get('error')
  const returnTo = params.get('returnTo') ?? '/'
  const message = error === null ? null : (ERRORS[error] ?? 'Sign-in failed.')

  return (
    <div className="erp-login">
      <div className="erp-login-card">
        <h1>ERP</h1>
        <p className="erp-muted">Sign in with your company Microsoft 365 account.</p>

        {message !== null && <div className="erp-error">{message}</div>}

        <a
          className="erp-button"
          href={`/api/auth/login?returnTo=${encodeURIComponent(returnTo)}`}
        >
          Sign in with Microsoft
        </a>

        <button
          type="button"
          className="erp-link-button erp-small"
          onClick={() => setShowBreakGlass((open) => !open)}
        >
          {showBreakGlass ? 'Hide' : 'Break-glass access'}
        </button>

        {showBreakGlass && <BreakGlassForm />}
      </div>
    </div>
  )
}

/**
 * Emergency access only. Visible so it can be used during an SSO outage, and
 * deliberately unglamorous so it is not used otherwise. Every attempt, success
 * or failure, raises an audit event and an alert.
 */
function BreakGlassForm(): JSX.Element {
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setBusy(true)
    setError(null)

    const form = new FormData(event.currentTarget)
    const response = await fetch('/api/auth/break-glass', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        email: form.get('email'),
        password: form.get('password'),
        totp: form.get('totp'),
      }),
    })

    setBusy(false)

    if (response.ok) {
      window.location.href = '/'
      return
    }

    const body = (await response.json().catch(() => ({}))) as { message?: string }
    setError(body.message ?? 'Sign-in failed')
  }

  return (
    <form className="erp-break-glass-form" onSubmit={(e) => void submit(e)}>
      <p className="erp-warning">
        Emergency access. Use only when single sign-on is unavailable — every
        attempt is logged and alerted on.
      </p>
      <label>
        Account
        {/* Not type="email": the account is `break-glass@local`, an identifier
            rather than a routable address. */}
        <input name="email" type="text" defaultValue="break-glass@local" required autoComplete="off" />
      </label>
      <label>
        Password
        <input name="password" type="password" required autoComplete="off" />
      </label>
      <label>
        Authenticator code
        <input name="totp" inputMode="numeric" pattern="\d{6}" required autoComplete="off" />
      </label>
      {error !== null && <div className="erp-error">{error}</div>}
      <button type="submit" className="erp-button erp-button-danger" disabled={busy}>
        {busy ? 'Checking…' : 'Break glass'}
      </button>
    </form>
  )
}